import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Cloud, Droplets, Sun, Wind, ThermometerSun, CloudRain } from "lucide-react";

const codeIcon = (code) => {
  if (code === 0) return Sun;
  if ([1, 2, 3].includes(code)) return Cloud;
  if ([45, 48].includes(code)) return Cloud;
  if (code >= 51 && code <= 67) return CloudRain;
  if (code >= 80 && code <= 99) return CloudRain;
  return Cloud;
};
const codeLabel = (c) =>
  ({ 0: "Clear", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast", 45: "Fog", 48: "Rime fog",
     51: "Light drizzle", 53: "Drizzle", 55: "Dense drizzle", 61: "Light rain", 63: "Rain",
     65: "Heavy rain", 71: "Light snow", 73: "Snow", 75: "Heavy snow", 80: "Showers",
     81: "Heavy showers", 95: "Thunderstorm" }[c] || "—");

export default function WeatherWidget({ location }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!location) return;
    setLoading(true);
    setError(null);
    api
      .get(`/weather?latitude=${location.latitude}&longitude=${location.longitude}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [location]);

  if (!location) {
    return (
      <div className="sage-card p-6" data-testid="weather-widget">
        <div className="label-overline mb-2">Weather</div>
        <div className="text-sm text-muted-foreground">
          Set your location to summon the local skies.
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="sage-card p-6 animate-pulse" data-testid="weather-widget">
        <div className="h-3 w-16 bg-muted rounded mb-3" />
        <div className="h-10 w-32 bg-muted rounded" />
      </div>
    );
  }

  if (error) {
    return <div className="sage-card p-6 text-sm text-destructive" data-testid="weather-widget">Could not load weather.</div>;
  }

  const cur = data.current;
  const daily = data.daily;
  const Icon = codeIcon(cur.weather_code);

  return (
    <div className="sage-card p-6 relative overflow-hidden" data-testid="weather-widget">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-overline">Today's sky</div>
          <div className="flex items-baseline gap-3 mt-1">
            <div className="font-serif text-5xl font-light text-foreground">
              {Math.round(cur.temperature_2m)}°
            </div>
            <div className="text-sm text-muted-foreground">{codeLabel(cur.weather_code)}</div>
          </div>
          <div className="text-xs text-muted-foreground mt-1">{location.label}</div>
        </div>
        <Icon className="w-10 h-10 text-accent" strokeWidth={1.2} />
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5 text-xs">
        <Stat icon={Droplets} label="Humidity" value={`${cur.relative_humidity_2m}%`} />
        <Stat icon={Wind} label="Wind" value={`${Math.round(cur.wind_speed_10m)} km/h`} />
        <Stat icon={ThermometerSun} label="UV index" value={`${cur.uv_index ?? "—"}`} />
        <Stat icon={CloudRain} label="Precip" value={`${cur.precipitation} mm`} />
      </div>

      <div className="mt-5 pt-4 border-t border-border">
        <div className="label-overline mb-2">5-day</div>
        <div className="grid grid-cols-5 gap-2">
          {daily.time.slice(0, 5).map((d, i) => {
            const DI = codeIcon(daily.weather_code[i]);
            return (
              <div key={d} className="text-center" data-testid={`forecast-day-${i}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {new Date(d).toLocaleDateString(undefined, { weekday: "short" })}
                </div>
                <DI className="w-5 h-5 mx-auto my-1 text-primary/80" strokeWidth={1.4} />
                <div className="text-[11px] font-medium">
                  {Math.round(daily.temperature_2m_max[i])}°
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {Math.round(daily.temperature_2m_min[i])}°
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-primary/70" strokeWidth={1.5} />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}
